import UUIDBase from '../UUIDBase'
import Location from '@/models/company/Location'
import User from '@/models/user/User'
import { Register } from '@/models/company/Register'
import Item from '@/models/inventory/Item'
import ItemSerial from '@/models/inventory/ItemSerial'
import ItemPack from "@/models/inventory/ItemPack";
import Vendor from "@/models/vendor/Vendor";
import InventoryReceiving from "@/models/inventory/InventoryReceiving";
import Customer from "@/models/customer/Customer";


export class OrderItemSerial extends UUIDBase {
    orderItemID = ''
    itemSerialID: string
    itemSerial?: ItemSerial
    received?: Date

    constructor(serialID: string) {
        super()
        this.itemSerialID = serialID
    }
}

export class PurchaseOrderItem extends UUIDBase {
    constructor(item: Item) {
        super()
        this.item = item
        this.itemID = item.id || ''

        if (item.isSerialized) {
            this.orderItemSerials = new Array<OrderItemSerial>()
        }
    }

    static from(json: PurchaseOrderItem | Record<string, unknown>) {
        const item = Item.from(json.item as Item)
        return Object.assign(new PurchaseOrderItem(item), json)
    }

    orderID = ''

    itemID = ''
    item: Item
    notes = ''

    quantity = 1
    receivedQuantity = 0
    orderItemSerials = new Array<OrderItemSerial>()
    serialIDs = new Array<string>()

    packInfo?: any = null;

    includesItemSerialID(serialID: string) {
        return !!this.orderItemSerials?.some((serial) => serial.itemSerialID === serialID)
    }

    insertItemSerialID(id: string) {
        if (!this.includesItemSerialID(id)) {
            this.orderItemSerials.push(new OrderItemSerial(id))
        }
    }

    populateInventoryTransferItemSerials(itemSerialIDs?: string[]) {
        if (itemSerialIDs) {
            this.resetOrderItemSerials()
            itemSerialIDs.forEach((id) => this.insertItemSerialID(id))
        }
    }

    resetOrderItemSerials() {
        if (this.orderItemSerials) this.orderItemSerials.length = 0
    }
}


export interface OrdersPaginated {
    data: Array<PurchaseOrder>,
    total: number
}

export class PurchaseOrder extends UUIDBase {
    static ENDPOINT = '/inventory/purchaseorders'
    externalID = ''

    dueDate?: Date

    locationID = ''
    location?: Location
    purchaseType: 'normal' | 'special' = 'normal'
    notes = ''

    manualNumber = ''
    pouchID = ''
    userID = ''
    user?: User

    vendorID = ''
    vendor?: Vendor

    customerID = ''
    customer?: Customer

    orderItems = new Array<PurchaseOrderItem>()
    inventoryReceivings = new Array<InventoryReceiving>()

    referenceableID?: string
    referenceableType?: string
    referenceableExternalID?: string

    static from(json: Record<string, unknown> | PurchaseOrder) {
        const o = Object.assign(new PurchaseOrder(), json)
        if (o.location) o.location = Location.from(o.location)
        if (o.user) o.user = User.from(o.user)
        if (o.vendor) o.vendor = Vendor.from(o.vendor)
        if (o.customer) o.customer = Customer.from(o.customer)
        o.orderItems = o.orderItems.map((poi) => PurchaseOrderItem.from(poi))
        return o
    }

    includes(item: Item) {
        return this.indexOf(item) >= 0
    }

    indexOf(item: Item) {
        return this.orderItems.findIndex((oi) => oi.item?.id === item.id)
    }

    incrementQuantity(item: Item) {
        for (const oi of this.orderItems) {
            if (oi.item?.id === item.id) {
                oi.quantity++
                break;
            }
        }
    }

    findByBarcode(barcode: string): Item | undefined {
        for (const oi of this.orderItems) {
            if (oi.item?.barcode === barcode) {
                return oi.item
            }
        }
    }

    findBySKU(sku: string): Item | undefined {
        for (const oi of this.orderItems) {
            if (oi.item?.sku === sku) {
                return oi.item
            }
        }
    }

    addItem(item: Item, appendToOrderItems = false) {
        let hasTheItem = false;
        let itemIndex = -1;

        // first loop for variant cases
        for (let i = 0; i < this.orderItems.length; i++) {
            if (!this.orderItems[i].packInfo?.packID) {
                if (item.itemParentID && item.id == this.orderItems[i].item.id) {
                    hasTheItem = true
                    itemIndex = i;
                    break
                }
            }
        }

        // normal cases
        if (!hasTheItem && !item.itemParentID) {
            for (let i = 0; i < this.orderItems.length; i++) {
                if (!this.orderItems[i].packInfo?.packID && item.id == this.orderItems[i].item.id) {
                    hasTheItem = true
                    itemIndex = i;
                }
            }
        }

        let oi
        if (this.includes(item) && !appendToOrderItems && hasTheItem) {
            // oi = this.orderItems[this.indexOf(item)]
            // this.incrementQuantity(item)
            this.orderItems[itemIndex].quantity++
        } else {
            // insert at index 0
            oi = new PurchaseOrderItem(item)
            this.orderItems.splice(0, 0, oi)
        }

        return oi
    }

    addItemPack(item: Item, itemPack: ItemPack, appendToOrderItems = false) {
        let hasTheBox = false;
        let boxIndex = -1;

        // first loop for variant cases
        for (let i = 0; i < this.orderItems.length; i++) {
            if (this.orderItems[i].packInfo?.packID) {
                if (item.itemParentID && item.id == this.orderItems[i].item.id) {
                    hasTheBox = true
                    boxIndex = i;
                    break
                }
            }
        }

        // normal cases
        if (!hasTheBox && !item.itemParentID) {
            for (let i = 0; i < this.orderItems.length; i++) {
                if (this.orderItems[i].packInfo?.packID && item.id == this.orderItems[i].item.id) {
                    hasTheBox = true
                    boxIndex = i;
                }
            }
        }


        let oi
        if (this.includes(item)  && !appendToOrderItems && hasTheBox) {
            // oi = this.orderItems[this.indexOf(item)]
            // this.incrementQuantity(item)
            this.orderItems[boxIndex].quantity++
        } else {
            // insert at index 0
            oi = new PurchaseOrderItem(item)
            oi.packInfo = {
                packID: itemPack.packID,
                packQuantity: itemPack.qty,
                soldQuantity: 1,
                itemPackID: itemPack.id,
                itemPack: itemPack,
                salePriceAmount: itemPack.salePriceAmount,
            }
            this.orderItems.splice(0, 0, oi)
        }

        return oi
    }

    removeItem(index: number) {
        this.orderItems.splice(index, 1)
    }

    empty() {
        return this.orderItems.length === 0
    }

    getOrderItemForPayload() {
        return this.orderItems.map((oi) => {
            const orderItemSerials = oi.item.isSerialized
                ? oi.serialIDs.map((serialId) => {
                    return {
                        itemSerialID: serialId,
                    }
                })
                : []
            if (orderItemSerials.length) {
                return {
                    itemID: oi.item.id,
                    quantity: oi.quantity,
                    notes: oi.notes,
                    orderItemSerials,
                    packInfo: oi.packInfo,
                }
            } else {
                return {
                    itemID: oi.itemID,
                    quantity: oi.quantity,
                    notes: oi.notes,
                    packInfo: oi.packInfo,
                }
            }
        })
    }

    preparePayload(user: User, register: Register, location: Location) {
        this.userID = user.id || ''
        this.locationID = location.id || ''

        return {
            customerID: this.customerID,
            manualNumber:this.manualNumber,
            pouchID: this.pouchID,
            dueDate: this.dueDate,
            userID: this.userID,
            vendorID: this.vendorID,
            locationID: this.locationID,
            orderItems: this.getOrderItemForPayload(),
            purchaseType: this.purchaseType,
            notes: this.notes
        }
    }
}
